import { tap } from 'rxjs'
import { action, computed, observable, runInAction } from 'mobx'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { BookkeepingProgressionStore } from '@/features/jobs/bookkeeping/BookkeepingProgressionStore'
import { ignoreAbortError } from '@/utils/abort-util'
import { BookkeepingProgressionInfo } from '@/features/jobs/bookkeeping/BookkeepingProgressionInfo'
import { task } from 'mobx-task'
import type {
  SnackbarFactory,
  SnackbarRetryWrapperFactory,
} from '@/features/shell'
import { BookkeepingConfirmationAccessDialogViewModel } from '@/features/jobs/details/bookkeeping-type/components/BookkeepingConfirmAccessDialog/BookkeepingConfirmationAccessDialogViewModel'
import type { BookkeepingPlatformSelectionDialogViewModelFactory } from '@/features/bookkeeping'
import type { BookkeepingStore } from '@/features/jobs/bookkeeping/BookkeepingStore'
import type {
  PlatformConnectionSelection,
  BookkeepingPlatformSelectionDialogViewModel,
} from '@/features/bookkeeping/components/BookkeepingPlatformSelectionDialog/BookkeepingPlatformSelectionDialogViewModel'

export class BookkeepingViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The platform selection dialog view model.
   */
  readonly bookkeepingPlatformSelectionDialogVm: BookkeepingPlatformSelectionDialogViewModel

  /**
   * The bookkeeping progression info.
   */
  @observable
  accessor bookkeepingProgressionInfo: BookkeepingProgressionInfo | null =
    BookkeepingProgressionInfo.none

  /**
   * The confirm access dialog view model.
   */
  readonly bookkeepingConfirmAccessDialogVm

  constructor(
    private readonly _job: SubmittedJob,
    private readonly _bookkeepingProgressionStore: BookkeepingProgressionStore,
    private readonly _bookkeepingStore: BookkeepingStore,
    private readonly _createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
    newSnackbar: SnackbarFactory,
    createBookkeepingPlatformSelectionDialogVm: BookkeepingPlatformSelectionDialogViewModelFactory,
    targetReturnUrl: () => URL,
  ) {
    this.bookkeepingConfirmAccessDialogVm =
      new BookkeepingConfirmationAccessDialogViewModel(
        this._job,
        this._bookkeepingStore,
        newSnackbar,
      )

    this.bookkeepingPlatformSelectionDialogVm =
      createBookkeepingPlatformSelectionDialogVm({
        target: {
          returnUrl: targetReturnUrl,
        },
        onProvideAccount: (accountToProvide) =>
          this.providePlatformConnection(accountToProvide),
      })

    this._lifecycle.manage(this.bookkeepingPlatformSelectionDialogVm)

    // Manages whenever the user wants to choose different account.
    this._lifecycle.manage(
      this.bookkeepingConfirmAccessDialogVm.chosenBookkeepingPlatform$.pipe(
        tap(this.handleChosenBookkeepingPlatform),
      ),
    )

    // Manages whenever the user confirms the access provided.
    this._lifecycle.manage(
      this.bookkeepingConfirmAccessDialogVm.confirmedProvidedAccess$.pipe(
        tap(this.handleConfirmProvidedAccess),
      ),
    )
  }

  /**
   * The platform connection.
   */
  @computed
  get platformConnection() {
    return this.bookkeepingProgressionInfo?.platformConnection
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()

    void this.fetchBookkeepingProgressionInfo(false, this._lifecycle.signal)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Shows the bookkeeping dialog.
   */
  @action.bound
  connectYourAccount() {
    switch (this.bookkeepingProgressionInfo?.accessStatus) {
      case 'ManagedAccessClientConfirmationRequested': {
        if (this.platformConnection) {
          if (this.platformConnection.type === 'Other') {
            throw new Error('Other platform connection is not supported')
          }

          this.bookkeepingConfirmAccessDialogVm.show(
            this.bookkeepingProgressionInfo,
          )
          break
        }

        // Fallback to platform selection dialog, but this should not happen.
        this.bookkeepingPlatformSelectionDialogVm.show()
        break
      }

      default:
        this.bookkeepingPlatformSelectionDialogVm.show()
    }
  }

  /**
   * Handles confirm provided access.
   */
  @action.bound
  handleConfirmProvidedAccess() {
    void this._reloadBookkeepingProgressionInfo()

    this.bookkeepingConfirmAccessDialogVm.confirmDialogVm.close()
    this.bookkeepingConfirmAccessDialogVm.close()
  }

  /**
   * Handles when a user wants to choose a different account.
   */
  @action.bound
  handleChosenBookkeepingPlatform() {
    this.bookkeepingPlatformSelectionDialogVm.show()
  }

  /**
   * Fetches the bookkeeping progression information for the job.
   */
  fetchBookkeepingProgressionInfo = task.resolved(
    async (skipCache: boolean, signal?: AbortSignal) => {
      const withRetry = this._createSnackbarRetryWrapper(
        'There was an issue retrieving your bookkeeping information',
        signal,
      )

      return withRetry(async () =>
        this._bookkeepingProgressionStore
          .fetchBookkeepingProgressionInfo(this._job.id, skipCache, signal)
          .then((b) => runInAction(() => (this.bookkeepingProgressionInfo = b)))
          .catch(ignoreAbortError),
      )
    },
  )

  /**
   * Provides the platform connection.
   */
  providePlatformConnection = task.resolved(
    async (platformConnectionSelection: PlatformConnectionSelection) => {
      const { confirmAccessRequired } =
        await this._bookkeepingStore.providePlatformConnection(
          this._job.id,
          platformConnectionSelection,
        )

      await this._reloadBookkeepingProgressionInfo()
      this.bookkeepingPlatformSelectionDialogVm.close()

      if (!confirmAccessRequired) {
        this.bookkeepingConfirmAccessDialogVm.close()
        await this._bookkeepingStore.markAccessAsGranted(this._job.id)
        return
      }

      this.bookkeepingConfirmAccessDialogVm.show(
        this.bookkeepingProgressionInfo,
      )
    },
  )

  /**
   * Reloads the bookkeeping progress information.
   */
  private async _reloadBookkeepingProgressionInfo() {
    await this.fetchBookkeepingProgressionInfo(true, this._lifecycle.signal)
  }
}
